import { Duration } from "luxon";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const oneHour = Duration.fromMillis(60 * 60 * 1000);
const threeHours = Duration.fromMillis(3 * oneHour.toMillis());
const twelveHours = Duration.fromMillis(12 * oneHour.toMillis());
const oneDay = Duration.fromMillis(24 * oneHour.toMillis());
const sevenDays = Duration.fromMillis(7 * oneDay.toMillis());
const twoWeeks = Duration.fromMillis(14 * oneDay.toMillis());
const thirtyDays = Duration.fromMillis(30 * oneDay.toMillis());

type Option = {
  label: string;
  period: Duration;
};

const options: Option[] = [
  { label: "last 30 days", period: thirtyDays },
  { label: "last 2 weeks", period: twoWeeks },
  { label: "last 7 days", period: sevenDays },
  { label: "last 24 hrs", period: oneDay },
  { label: "last 12 hrs", period: twelveHours },
  { label: "last 3 hrs", period: threeHours },
  { label: "last hour", period: oneHour },
];

type Result = {
  period: Duration;
  setPeriod: (period: Duration) => void;
  options: Option[];
};

const useTimePeriod = (): Result => {
  const [searchParams, setSearchParams] = useSearchParams();

  // set the period to either
  //   (1) the value in the query string
  //   (2) the value in local storage
  //   (3) thirty days
  const period = useMemo(() => {
    const cases = [
      searchParams.get("period"),
      localStorage.getItem("time_period"),
    ];
    for (const str of cases) {
      const duration = str
        ? Duration.fromISO(str)
        : Duration.invalid("not set");
      if (duration.isValid) {
        return duration;
      }
    }
    return thirtyDays;
  }, [searchParams.get("period")]);

  // when the time period is updated, set it in local storage and on the
  // query string
  const setPeriod = useCallback((value: Duration) => {
    const str = value.toISO();
    if (str) {
      localStorage.setItem("time_period", str);
      setSearchParams((q) => {
        q.set("period", str);
        return q;
      });
    } else {
      localStorage.removeItem("time_period");
      setSearchParams((q) => {
        q.delete("period");
        return q;
      });
    }
  }, []);

  return { period, setPeriod, options };
};
export default useTimePeriod;
